import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppConstants } from '../app.constants';

import { Course } from '../model/course.model';
import { CourseDate, CourseSessionDate } from '../model/course-date.model';
import { CourseEnglishRequirement } from '../model/course-english-requirement.model';
import { CourseSummary } from '../model/course-summary.model';
import { CourseFilter } from '../model/course-filter.model';
import { CourseEligibilityCheckResult } from '../model/course-eligibility-check-result.model';
import { InterviewQuestionnaireSection } from '../model/interview-questionnaire.model';
import { UtilService } from './util.service';
import { ExceptionService } from './exception.service';
import { LoaderEnabled } from './loader.service';

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  constructor(
    private http: HttpClient,
    private utilService: UtilService,
    private exceptionService: ExceptionService
  ) { }

  public getCourseAuditTrail(courseId: number): Observable<any> {
    const url = `${AppConstants.endpoint.course.audit_trail}?courseId=${courseId}`;
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public getCourses(): Observable<Course[]> {
    return this.http.get<Course[]>(`${AppConstants.endpoint.course.base}/getcourses`)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  createCourseWithGeneralInfo(payload: any): Observable<Course> {
    return this.http.post<Course>(AppConstants.endpoint.course.base, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getCourseSummaries(courseFilter: CourseFilter, pageNumber: number): Observable<HttpResponse<CourseSummary[]>> {
    let params = new HttpParams();
    params = params.append('pageNumber', pageNumber.toString());

    return this.http.post<CourseSummary[]>(AppConstants.endpoint.course.summaries, courseFilter,
      { params: params, headers: this.utilService.getJSONHeader(), observe: 'response' })
      .pipe(catchError(this.exceptionService.handleError));

    // return this.http.post<CourseSummary[]>(AppConstants.endpoint.course.summaries, courseFilter, this.utilService.getHeaders())
    //   .pipe(catchError(this.exceptionService.handleError));

  }

  @LoaderEnabled()
  getCourseDateDetails(sessionId: number): Observable<Course> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.getCourseDateDetails, sessionId);
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getBatchDetails(batchId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.batch_details, batchId);
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  getBatchStats(batchId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.batch_stats, batchId);
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  getBatchStudentsByPayment(batchId: number, paymentStatus: string): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.batch_students_by_payment, batchId);
    let params = new HttpParams().set('paymentStatus', paymentStatus);
    return this.http.get<any>(url, { params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  cloneBatch(batchId: number, payload: { session_name: string; transfer_waitlisted: boolean }): Observable<any> {
    const url = this.utilService.prepareUrl(AppConstants.endpoint.student.batch_clone, batchId);
    return this.http.post<any>(url, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getCourseDates(courseId: number, reqPayload: any): Observable<any> {
    let params = new HttpParams();
    const filter = reqPayload.filter;
    const defaultParams = reqPayload.defaultParams;

    if (filter != null) {
      Object.keys(filter).forEach(key => {
        params = params.append(key, filter[key]?.toString());
      });
    }
    if(defaultParams) {
      params = params.append('page', defaultParams.currentPage.toString());
      params = params.append('size', defaultParams.itemsPerPage.toString());
    }
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.course_dates, courseId);
    return this.http.get<any>(url, {params: params})
      .pipe(catchError(this.exceptionService.handleError));
  }
  @LoaderEnabled()
  getAllCourseDates(instituteId: number, reqPayload: any): Observable<any> {
    let params = new HttpParams();
    const filter = reqPayload.filter;
    const defaultParams = reqPayload.defaultParams;

    if (filter != null) {
      Object.keys(filter).forEach(key => {
        params = params.append(key, filter[key]?.toString());
      });
    }
    if(defaultParams) {
      params = params.append('page', defaultParams.currentPage.toString());
      params = params.append('size', defaultParams.itemsPerPage.toString());
    }
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.getAllCoursesWithSessions, instituteId);
    return this.http.get<any>(url, {params: params})
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getCourse(courseId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.get, courseId);
    return this.http.get<Course>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getBatchStatusCounts(courseId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.batch_status_counts, courseId);
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  addCourseSession(courseId: number, payload: CourseSessionDate): Observable<CourseSessionDate> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.date, courseId);
    return this.http.post<CourseSessionDate>(url, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public checkCourseEligibility(courseId: number, userId: number, value: any): Observable<CourseEligibilityCheckResult> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.check_eligibility, courseId);
    let params = new HttpParams();
    if (userId != null) {
      params = params.append('userId', userId.toString());
    }

    return this.http.post<CourseEligibilityCheckResult>(url, value, { headers: this.utilService.getJSONHeader(), params: params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public updateCourseGeneralInfo(courseId: number, value: any): Observable<Course> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.updateCourseWithGeneralInfo, courseId);
    return this.http.put<Course>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  /** Course-owned interview questionnaire endpoints. These do not alter existing course-update APIs. */
  public getInterviewQuestionnaireTemplate(courseId: number): Observable<any> {
    const url = this.utilService.prepareUrl(AppConstants.endpoint.course.interview_questions, courseId);
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public saveInterviewQuestionnaireTemplate(
    courseId: number,
    sections: InterviewQuestionnaireSection[]
  ): Observable<any> {
    const url = this.utilService.prepareUrl(AppConstants.endpoint.course.interview_questions, courseId);
    return this.http.post<any>(url, { sections }, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateCourseDescription(courseId: number, value: any): Observable<Course> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.description, courseId);
    return this.http.put<Course>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateCourseAdmissionRequirement(courseId: number, value: any): Observable<Course> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.admission_req, courseId);
    return this.http.put<Course>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateCourseFeeStructure(courseId: number, value: any): Observable<Course> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.fees, courseId);
    return this.http.put<Course>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public addCourseEnglishRequirement(courseId: number, value: any): Observable<CourseEnglishRequirement> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.requirement_eng, courseId);
    return this.http.post<CourseEnglishRequirement>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateCourseEnglishRequirement(courseId: number, courseEnglishRequirementId: number, value: any): Observable<CourseEnglishRequirement> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.requirement_eng, courseId);
    url = url + '/' + courseEnglishRequirementId;
    return this.http.put<CourseEnglishRequirement>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public deleteCourseEnglishRequirement(courseId: number, courseEnglishRequirementId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.requirement_eng, courseId);
    url = url + '/' + courseEnglishRequirementId;
    return this.http.delete<any>(url, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateCourseAcademicRequirement(courseId: number, value: any): Observable<Course> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.requirement_academic, courseId);
    return this.http.put<Course>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public addCourseDate(courseId: number, value: any): Observable<CourseDate> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.date, courseId);
    return this.http.post<CourseDate>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public updateCourseDate(courseId: number, courseDateId: number, value: any): Observable<CourseDate> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.date, courseId);
    url = url + '/' + courseDateId;
    return this.http.put<CourseDate>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public deleteCourseDate(courseId: number, courseDateId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.date, courseId);
    url = url + '/' + courseDateId;
    return this.http.delete<any>(url, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public publishCourse(courseId: number): Observable<Course> {
    let value = '';
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.publish, courseId);
    return this.http.put<Course>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public deleteCourse(courseId: number): Observable<Course> {
    let value = '';
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.delete, courseId);
    return this.http.put<Course>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public getAdmissionRequirements(courseId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.admission_requirements, courseId);
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  saveAdmissionRequirements(payload: any, courseId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.admission_requirements, courseId);
    return this.http.post<any>(url, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

   @LoaderEnabled()
  updateAdmissionRequirements(courseId: number, payload: any): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.admission_requirements, courseId);
    return this.http.put<any>(url, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getMoodleCoursesList(): Observable<any> {
    return this.http.get<any>(AppConstants.endpoint.moodle.getMoodleCoursesList)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public softDeleteCourse(courseId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.softDeleteCourse, courseId);
    return this.http.delete<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public softDeleteBatch(courseDateId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.course.softDeleteBatch, courseDateId);
    return this.http.delete<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

}
